import { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  Snackbar,
  Alert,
} from "@mui/material";

const CustomTextfield = ({ label, ...rest }) => {
  return (
    <FormControl variant="standard" fullWidth sx={{ my: 1 }}>
      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
        {label}
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        sx={{
          "& .MuiInputBase-root": {
            padding: 0,
            borderRadius: "8px",
            backgroundColor: "#fff",
          },
          "& .MuiInputBase-input": {
            fontSize: "14px",
            padding: "12px",
          },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": {
              borderColor: "var(--primary-color)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "var(--primary-color)",
              borderWidth: "2px",
            },
          },
        }}
        {...rest}
      />
    </FormControl>
  );
};

const regexEmail = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

const ContactUsForm = ({ subjectText }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: subjectText || "",
    message: "",
  });
  const [errorFields, setErrorFields] = useState({
    name: false,
    email: false,
    message: false,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (subjectText) {
      setFormData((prev) => ({ ...prev, subject: subjectText }));
    }
  }, [subjectText]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (!value && name !== "phone" && name !== "subject") {
      setErrorFields({ ...errorFields, [name]: true });
    } else {
      if (name === "email") {
        let emailValid = regexEmail.test(value);
        setErrorFields({ ...errorFields, email: !emailValid });
      } else {
        setErrorFields({ ...errorFields, [name]: false });
      }
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let hasError = false;
    const newErrors = { ...errorFields };
    
    if (!formData.name) {
      newErrors.name = true;
      hasError = true;
    }
    if (!formData.email || !regexEmail.test(formData.email)) {
      newErrors.email = true;
      hasError = true;
    }
    if (!formData.message) {
      newErrors.message = true;
      hasError = true;
    }
    
    setErrorFields(newErrors);
    
    if (!hasError) {
      // Simulate form submission
      setSnackbar({ open: true, message: "Message sent successfully! We'll get back to you soon.", severity: "success" });
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: "#f8fafc",
        p: { xs: 3, md: 4 },
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        Send Us a{" "}
        <Box
          component="span"
          sx={{
            background: "linear-gradient(135deg, var(--primary-color), var(--primary-color-2))",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Message
        </Box>
      </Typography>
      <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
        Fill out the form below and we'll get back to you within 24 hours.
      </Typography>
      
      <form onSubmit={handleSubmit}>
        <CustomTextfield
          label="Full Name *"
          name="name"
          type="text"
          placeholder="John Doe"
          value={formData.name}
          error={errorFields.name}
          onChange={handleInputChange}
        />

        <CustomTextfield
          label="Email Address *"
          name="email"
          type="email"
          placeholder="john@example.com"
          value={formData.email}
          error={errorFields.email}
          helperText={errorFields.email ? "Please enter a valid email" : ""}
          onChange={handleInputChange}
        />

        <CustomTextfield
          label="Phone Number"
          name="phone"
          type="tel"
          placeholder="+1 (469) 123-4567"
          value={formData.phone}
          onChange={handleInputChange}
        />

        <CustomTextfield
          label="Subject"
          name="subject"
          type="text"
          placeholder="How can we help you?"
          value={formData.subject}
          onChange={handleInputChange}
        />

        <CustomTextfield
          label="Message *"
          name="message"
          multiline
          rows={4}
          placeholder="Tell us about your project or inquiry..."
          value={formData.message}
          error={errorFields.message}
          onChange={handleInputChange}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{
            mt: 2,
            py: 1.5,
            backgroundColor: "var(--primary-color)",
            color: "#fff",
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "var(--primary-color-2)",
            },
          }}
        >
          Send Message
        </Button>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactUsForm;
